import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useMatchLogStore,
  type MatchEvent,
} from "../../../store/useMatchLogStore";
import { deleteMatchEvent, updateMatchEvent } from "../../../lib/loggerApi";
import type { GoalPlacement } from "../utils/goalFrame";

interface UseCockpitEventHandlersParams {
  t: (...args: any[]) => any;
  isAdmin: boolean;
  isConnected: boolean;
  cockpitLocked: boolean;
  lockReason?: string;
  pendingAcks: Record<string, unknown>;
  undoStack: string[];
  liveEvents: MatchEvent[];
  queuedEvents: MatchEvent[];
  removeLiveEventByClientId: (clientId: string) => void;
  removeQueuedEvent: (event: MatchEvent) => void;
  removeUndoCandidate: (clientId: string) => void;
  undoEvent: (event: MatchEvent) => void;
  removeQueuedEventByClientId: (clientId: string) => void;
  rejectPendingAck: (clientId: string) => void;
  removeLiveEventById: (eventId: string) => void;
  hydrateEvents: () => Promise<void>;
  updateEventNotes: (event: MatchEvent, notes: string | null) => void;
  upsertLiveEvent: (event: MatchEvent) => void;
  showToast: (message: string) => void;
}

/** Result of `handleDeletePendingEvent` — see that function's own comment for
 * why this is a discriminated union rather than a bare void/boolean. */
export type DeletePendingEventOutcome =
  | { status: "removed" }
  | { status: "already-confirmed"; event: MatchEvent }
  | { status: "not-found" };

interface UseCockpitEventHandlersResult {
  undoError: string | null;
  undoDisabled: boolean;
  handleUndoLastEvent: () => Promise<void>;
  handleDeletePendingEvent: (clientId: string) => DeletePendingEventOutcome;
  handleDeleteLoggedEvent: (event: MatchEvent) => Promise<void>;
  handleUpdateEventNotes: (
    event: MatchEvent,
    notes: string | null,
  ) => Promise<void>;
  handleUpdateEventData: (
    event: MatchEvent,
    updates: Partial<MatchEvent>,
  ) => Promise<void>;
  handleVarCornerToGoalKick: (event: MatchEvent) => Promise<void>;
  handleUpdateGoalPlacement: (
    event: MatchEvent,
    placement: GoalPlacement,
  ) => Promise<void>;
}

export const useCockpitEventHandlers = ({
  t,
  isAdmin,
  isConnected,
  cockpitLocked,
  lockReason,
  pendingAcks,
  undoStack,
  liveEvents,
  queuedEvents,
  removeLiveEventByClientId,
  removeQueuedEvent,
  removeUndoCandidate,
  undoEvent,
  removeQueuedEventByClientId,
  rejectPendingAck,
  removeLiveEventById,
  hydrateEvents,
  updateEventNotes,
  upsertLiveEvent,
  showToast,
}: UseCockpitEventHandlersParams): UseCockpitEventHandlersResult => {
  const [undoError, setUndoError] = useState<string | null>(null);

  const lastUndoClientId = undoStack.length
    ? undoStack[undoStack.length - 1]
    : null;

  const lastUndoEvent = useMemo(() => {
    if (!lastUndoClientId) return null;
    return (
      liveEvents.find((event) => event.client_id === lastUndoClientId) ||
      queuedEvents.find((event) => event.client_id === lastUndoClientId) ||
      null
    );
  }, [lastUndoClientId, liveEvents, queuedEvents]);

  const undoRequiresConnection = Boolean(
    lastUndoClientId && (pendingAcks[lastUndoClientId] || lastUndoEvent?._id),
  );

  // In-flight undo guard: client_ids for which an `undo` command has been
  // sent over the WebSocket but whose ack has not yet arrived. The optimistic
  // removal happens in handleUndoAck (useMatchSocket.ts), not at click time,
  // so lastUndoClientId/lastUndoEvent do not move until the ack lands — a
  // second click (or a non-button caller, e.g. the E2E harness's
  // `undoLastEvent`, which bypasses TeamSelector's disabled state and its
  // 700ms touch/synthetic-click dedupe entirely) would otherwise re-send
  // `undo` for the SAME client_id. Self-heals: once the ack pops a client_id
  // off `undoStack`, it is pruned from this set below.
  const [inFlightUndoIds, setInFlightUndoIds] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    // Bail out BEFORE touching the state dispatcher when there is nothing to
    // prune. `undoStack` gets a fresh array reference on essentially every
    // logged event (`pushUndoCandidate` runs on both optimistic send and ack,
    // for every event type), so this effect re-runs at that same rate.
    // Calling `setInFlightUndoIds` unconditionally (previously relying only
    // on the updater's own same-reference bail-out below) still registers as
    // a setState-inside-useEffect on every one of those re-runs; under heavy
    // event volume with no yield between renders, enough of those stack to
    // trip React's nested-update-depth guard even though the update itself
    // is always a no-op while no undo is in flight.
    if (inFlightUndoIds.size === 0) return;
    setInFlightUndoIds((prev) => {
      if (prev.size === 0) return prev;
      const stillPending = new Set(undoStack);
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (stillPending.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [undoStack, inFlightUndoIds]);

  const isLastUndoInFlight = Boolean(
    lastUndoClientId && inFlightUndoIds.has(lastUndoClientId),
  );

  const undoDisabled =
    !lastUndoEvent ||
    cockpitLocked ||
    isLastUndoInFlight ||
    (undoRequiresConnection && !isConnected);

  // Deletes a PENDING (unacked, no `_id`) event from the offline queue —
  // local-only, never hits the server. Callers must pick this vs. the
  // confirmed-delete (server DELETE, `handleDeleteLoggedEvent`) path based
  // on a check made HERE, at call time, from the store's CURRENT state —
  // not from the `liveEvents`/`queuedEvents` params closed over by this
  // callback. Those params are only ever as fresh as React's LAST COMMIT:
  // a Zustand `set()` (e.g. the WS ack handler writing `_id` into the store
  // the instant the server confirms an event) is synchronous, but the
  // re-render it schedules is not — there is a real window, at least one
  // frame wide and wider under load, where the store has already moved and
  // this hook's own params have not caught up. A tap landing in that
  // window would read a stale "still pending" params snapshot and wrongly
  // take the local-removal branch below. So this reads `useMatchLogStore
  // .getState()` directly instead — the same idiom `useCockpitInteraction
  // Handlers.ts`'s `logCardForPlayer`/`logCardForStaff` use (`latestState`)
  // for the identical reason. Since JS execution is single-threaded, this
  // read and the synchronous removal calls that may follow it below happen
  // in one uninterruptible span — nothing can land between them, so the
  // window collapses to zero.
  // If the event has already gained an `_id` by the time this runs, it is a
  // REAL confirmed event now — removing it locally here would desync the
  // UI from the server (still exists server-side, invisible locally until
  // the next hydrate) without ever deleting it. So this bails out instead
  // of touching the stores, and hands the fresh, now-confirmed event back
  // to the caller to route through the server-delete path. A late-arriving
  // ack for a client_id already removed here is handled gracefully by
  // `handleAckResult` (`useMatchSocket.ts`) — `rejectPendingAck` already
  // cleared the `pendingAcks` entry, so the ack finds no entry and just
  // warns/no-ops, never re-adding an orphaned event.
  const handleDeletePendingEvent = useCallback(
    (clientId: string): DeletePendingEventOutcome => {
      if (!clientId) return { status: "not-found" };
      const latestState = useMatchLogStore.getState();
      const current =
        latestState.liveEvents.find((event) => event.client_id === clientId) ??
        latestState.queuedEvents.find((event) => event.client_id === clientId);
      if (!current) return { status: "not-found" };
      if (current._id) {
        return { status: "already-confirmed", event: current };
      }
      removeLiveEventByClientId(clientId);
      removeQueuedEventByClientId(clientId);
      rejectPendingAck(clientId);
      return { status: "removed" };
    },
    [removeLiveEventByClientId, removeQueuedEventByClientId, rejectPendingAck],
  );

  const handleDeleteLoggedEvent = useCallback(
    async (event: MatchEvent) => {
      if (!event._id || !isAdmin) return;
      try {
        await deleteMatchEvent(event._id);
        removeLiveEventById(event._id);
        if (event.client_id) {
          removeQueuedEventByClientId(event.client_id);
        }
        await hydrateEvents();
      } catch (error) {
        console.error("Failed to delete event", error);
        showToast(t("deleteEventFailed", "Unable to delete event right now."));
      }
    },
    [
      hydrateEvents,
      isAdmin,
      removeLiveEventById,
      removeQueuedEventByClientId,
      showToast,
      t,
    ],
  );

  const handleUpdateEventNotes = useCallback(
    async (event: MatchEvent, notes: string | null) => {
      updateEventNotes(event, notes);
      if (!event._id) return;
      try {
        const updated = await updateMatchEvent(event._id, { notes });
        upsertLiveEvent(updated);
      } catch (error) {
        console.error("Failed to update event notes", error);
        showToast(t("notesUpdateFailed", "Unable to update notes right now."));
      }
    },
    [showToast, t, updateEventNotes, upsertLiveEvent],
  );

  const handleUpdateEventData = useCallback(
    async (event: MatchEvent, updates: Partial<MatchEvent>) => {
      if (!event._id || !isAdmin) return;
      try {
        const updated = await updateMatchEvent(event._id, updates);
        upsertLiveEvent(updated);
        showToast(t("eventUpdated", "Event updated successfully."));
      } catch (error) {
        console.error("Failed to update event", error);
        showToast(t("eventUpdateFailed", "Unable to update event right now."));
      }
    },
    [isAdmin, showToast, t, upsertLiveEvent],
  );

  // R1 / PMA-COCK-076: dedicated one-tap VAR correction — Corner Kick -> Goal
  // Kick, in place. Hardcoded to this single transform (no generic
  // swap-any-event UI is exposed). Rides the existing PUT /events/{id} edit
  // path (same one PMA-COCK-049 refinement chips and the outcome editor
  // use) — no WS event/ack/undo shape is touched. Deliberately NOT gated by
  // isAdmin (unlike handleUpdateEventData): VAR overturns happen live and
  // must be correctable by whoever is operating the cockpit. `data` is a
  // full-replace on the backend ($set), so the full original `data` object
  // is spread forward with only `set_piece_type` changed — no field is
  // dropped. `timestamp` is never included in the update payload, so the
  // original event timestamp is preserved untouched, both server-side and
  // in the response doc used to refresh local state.
  const handleVarCornerToGoalKick = useCallback(
    async (event: MatchEvent) => {
      if (
        !event._id ||
        event.type !== "SetPiece" ||
        event.data?.set_piece_type !== "Corner"
      ) {
        return;
      }
      try {
        const updated = await updateMatchEvent(event._id, {
          data: {
            ...event.data,
            set_piece_type: "Goal Kick",
            var_overturned_from: "Corner",
          },
        });
        upsertLiveEvent(updated);
      } catch (error) {
        console.error(
          "Failed to apply VAR corner-to-goal-kick correction",
          error,
        );
        showToast(
          t(
            "varCornerToGoalKickFailed",
            "Unable to apply the VAR correction right now.",
          ),
        );
      }
    },
    [showToast, t, upsertLiveEvent],
  );

  // PMA-COCK-182: review/feed edit of the goal-mouth placement — Corner Kick
  // → Goal Kick's non-admin precedent (PMA-COCK-076) above: a placement is
  // descriptive, not authoritative (no stat/score/xG path reads it), so any
  // cockpit operator can fill it in later, not just an admin. Sends ONLY the
  // `goal_placement` key — the backend's key-wise `data` merge
  // (PMA-EVENT-018) is what keeps every other `data` field intact, so this
  // deliberately does NOT spread `event.data` forward.
  const handleUpdateGoalPlacement = useCallback(
    async (event: MatchEvent, placement: GoalPlacement) => {
      if (!event._id) return;
      try {
        const updated = await updateMatchEvent(event._id, {
          data: { goal_placement: { ...placement, source: "review" } },
        });
        upsertLiveEvent(updated);
        showToast(t("eventUpdated", "Event updated successfully."));
      } catch (error) {
        console.error("Failed to update goal placement", error);
        showToast(t("eventUpdateFailed", "Unable to update event right now."));
      }
    },
    [showToast, t, upsertLiveEvent],
  );

  const handleUndoLastEvent = useCallback(async () => {
    if (cockpitLocked) {
      setUndoError(
        lockReason || t("undoLocked", "Cannot undo while cockpit is locked."),
      );
      return;
    }
    if (!lastUndoClientId || !lastUndoEvent) {
      setUndoError(t("undoUnavailable", "No event available to undo."));
      return;
    }
    // Belt-and-suspenders: undoDisabled already covers the button, but
    // non-button callers (E2E harness) can invoke this directly.
    if (inFlightUndoIds.has(lastUndoClientId)) {
      return;
    }

    setUndoError(null);

    const previousUndoClientId =
      undoStack.length > 1 ? undoStack[undoStack.length - 2] : null;
    const previousUndoEvent = previousUndoClientId
      ? liveEvents.find((event) => event.client_id === previousUndoClientId) ||
        queuedEvents.find((event) => event.client_id === previousUndoClientId)
      : null;

    const lastCardType = String(
      lastUndoEvent.data?.card_type || "",
    ).toLowerCase();
    const previousCardType = String(
      previousUndoEvent?.data?.card_type || "",
    ).toLowerCase();
    const shouldCascadeUndoSecondYellow =
      Boolean(previousUndoClientId) &&
      Boolean(previousUndoEvent) &&
      lastUndoEvent.type === "Card" &&
      previousUndoEvent?.type === "Card" &&
      lastCardType.includes("red") &&
      previousCardType.includes("yellow (second)") &&
      lastUndoEvent.player_id === previousUndoEvent.player_id &&
      lastUndoEvent.team_id === previousUndoEvent.team_id &&
      lastUndoEvent.period === previousUndoEvent.period;

    const eventsToUndo =
      shouldCascadeUndoSecondYellow && previousUndoEvent
        ? [lastUndoEvent, previousUndoEvent]
        : [lastUndoEvent];

    const removeLocally = (targetEvent: MatchEvent, targetClientId: string) => {
      removeLiveEventByClientId(targetClientId);
      removeQueuedEvent(targetEvent);
      removeUndoCandidate(targetClientId);
    };

    const hasOfflineOnlyTargets = eventsToUndo.every((event) => {
      if (!event.client_id) return false;
      return !pendingAcks[event.client_id] && !event._id;
    });

    if (hasOfflineOnlyTargets || !undoRequiresConnection) {
      eventsToUndo.forEach((event) => {
        if (!event.client_id) return;
        removeLocally(event, event.client_id);
      });
      return;
    }

    const idsToMark = eventsToUndo
      .map((event) => event.client_id)
      .filter((id): id is string => Boolean(id));

    if (idsToMark.length) {
      setInFlightUndoIds((prev) => {
        const next = new Set(prev);
        idsToMark.forEach((id) => next.add(id));
        return next;
      });
    }

    try {
      eventsToUndo.forEach((event) => {
        undoEvent(event);
      });
    } catch (error) {
      console.error("Undo failed", error);
      setUndoError(
        t(
          "undoFailed",
          "Unable to undo the last event. Try again when the connection is back.",
        ),
      );
      // Sending failed synchronously (no ack will ever arrive) — clear the
      // guard immediately so the operator can retry.
      if (idsToMark.length) {
        setInFlightUndoIds((prev) => {
          if (prev.size === 0) return prev;
          const next = new Set(prev);
          idsToMark.forEach((id) => next.delete(id));
          return next;
        });
      }
    }
  }, [
    cockpitLocked,
    inFlightUndoIds,
    lastUndoClientId,
    lastUndoEvent,
    liveEvents,
    lockReason,
    pendingAcks,
    queuedEvents,
    removeLiveEventByClientId,
    removeQueuedEvent,
    removeUndoCandidate,
    t,
    undoEvent,
    undoRequiresConnection,
    undoStack,
  ]);

  return {
    undoError,
    undoDisabled,
    handleUndoLastEvent,
    handleDeletePendingEvent,
    handleDeleteLoggedEvent,
    handleUpdateEventNotes,
    handleUpdateEventData,
    handleVarCornerToGoalKick,
    handleUpdateGoalPlacement,
  };
};
